import Box from '@mui/material/Box'
import { Controller } from 'react-hook-form'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import AirlinesIcon from '@mui/icons-material/Airlines'
import BadgeIcon from '@mui/icons-material/Badge'
import SmartphoneIcon from '@mui/icons-material/Smartphone'
import SubscriptionsIcon from '@mui/icons-material/Subscriptions'
import EmailIcon from '@mui/icons-material/Email'
import UserAvatar from '../../../layouts/navbar/UserAvatar'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
import { useMediaQuery } from '@mui/material'
import { useAuth } from '../../../contexts/AuthContext'
import { useMemo } from 'react'

function capitalizeFirstLetter(string) {
    if (!string || string === '') return ''
    return string.charAt(0).toUpperCase() + string.slice(1)
}

export default function ProfileForm({ control, disabled }) {
    const theme = useTheme()
    const { auth } = useAuth()
    const bigScreen = useMediaQuery(theme.breakpoints.up('sm'))
    // Memoize avatar props
    const avatarProps = useMemo(
        () => ({
            firstName: auth?.firstName,
            lastName: auth?.lastName,
        }),
        [auth?.firstName, auth?.lastName]
    )

    return (
        <Box
            display="flex"
            alignItems="center"
            justifyContent="space-around"
            flexBasis="content"
            width={'100%'}
        >
            <Grid
                container
                spacing={2}
                columns={{ xs: 6, sm: 12 }}
                sx={{ width: { xs: '80vw', sm: '60vw' } }}
            >
                <Grid size={{ xs: 6, sm: 6 }}>
                    <Controller
                        control={control}
                        name="firstName"
                        render={({ field }) => (
                            <TextField
                                {...field}
                                required
                                label="First Name"
                                id="firstName"
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <AccountCircleIcon
                                                    sx={{
                                                        color: disabled
                                                            ? 'text.disabled'
                                                            : 'text.primary',
                                                    }}
                                                />
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                                variant="standard"
                                fullWidth
                                value={field.value}
                                inputRef={field.ref}
                                onChange={(e) => {
                                    const capitilized = capitalizeFirstLetter(
                                        e.target.value
                                    ).trim()
                                    field.onChange(capitilized)
                                }}
                                disabled={disabled}
                                // error={!userData?.firstName}
                            />
                        )}
                    />
                </Grid>

                <Grid size={{ xs: 6, sm: 6 }}>
                    <Controller
                        control={control}
                        name="lastName"
                        render={({ field }) => (
                            <TextField
                                {...field}
                                required
                                label="Last Name"
                                id="lastName"
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <AccountCircleIcon
                                                    sx={{
                                                        color: disabled
                                                            ? 'text.disabled'
                                                            : 'text.primary',
                                                    }}
                                                />
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                                variant="standard"
                                fullWidth
                                value={field.value}
                                onChange={(e) => {
                                    const capitilized = capitalizeFirstLetter(
                                        e.target.value
                                    ).trim()
                                    field.onChange(capitilized)
                                }}
                                disabled={disabled}
                                // error={!userData?.lastName}
                            />
                        )}
                    />
                </Grid>

                <Grid size={{ xs: 6, sm: 6 }}>
                    <Controller
                        control={control}
                        name="airline_id"
                        render={({ field }) => (
                            <TextField
                                {...field}
                                required
                                label="Airline"
                                id="airline_id"
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <AirlinesIcon
                                                    sx={{
                                                        color: 'text.disabled',
                                                    }}
                                                />
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                                variant="standard"
                                fullWidth
                                value={field.value}
                                disabled={true}
                            />
                        )}
                    />
                </Grid>

                <Grid size={{ xs: 6, sm: 6 }}>
                    <Controller
                        control={control}
                        name="staffNumber"
                        render={({ field }) => (
                            <TextField
                                {...field}
                                label="Staff Number"
                                id="staffNumber"
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <BadgeIcon
                                                    sx={{
                                                        color: disabled
                                                            ? 'text.disabled'
                                                            : 'text.primary',
                                                    }}
                                                />
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                                variant="standard"
                                fullWidth
                                value={field.value}
                                onChange={field.onChange}
                                disabled={disabled}
                            />
                        )}
                    />
                </Grid>

                <Grid size={{ xs: 6, sm: 6 }}>
                    <Controller
                        control={control}
                        name="mobile"
                        render={({ field }) => (
                            <TextField
                                {...field}
                                label="Mobile"
                                id="mobile"
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SmartphoneIcon
                                                    sx={{
                                                        color: disabled
                                                            ? 'text.disabled'
                                                            : 'text.primary',
                                                    }}
                                                />
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                                variant="standard"
                                fullWidth
                                value={field.value}
                                onChange={field.onChange}
                                disabled={disabled}
                            />
                        )}
                    />
                </Grid>

                <Grid size={{ xs: 6, sm: 6 }}>
                    <Controller
                        control={control}
                        name="status"
                        render={({ field }) => (
                            <TextField
                                {...field}
                                required
                                label="Subscription Status"
                                id="status"
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SubscriptionsIcon
                                                    sx={{
                                                        color: 'text.disabled',
                                                    }}
                                                />
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                                variant="standard"
                                fullWidth
                                value={capitalizeFirstLetter(field.value)}
                                // onChange={(e) => {
                                //     const capitilized = capitalizeFirstLetter(
                                //         e.target.value
                                //     ).trim()
                                //     field.onChange(capitilized)
                                // }}
                                disabled={true}
                            />
                        )}
                    />
                </Grid>

                <Grid size={{ xs: 6, sm: 12 }}>
                    <Controller
                        control={control}
                        name="email"
                        render={({ field }) => (
                            <TextField
                                {...field}
                                required
                                label="Email"
                                id="email"
                                fullWidth
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <EmailIcon
                                                    sx={{
                                                        color: 'text.disabled',
                                                    }}
                                                />
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                                variant="standard"
                                value={field.value}
                                disabled={true}
                            />
                        )}
                    />
                </Grid>
                <Grid size={{ xs: 6, sm: 6 }}>
                    <Typography
                        variant="caption"
                        sx={{
                            color: theme.palette.text.disabled,
                        }}
                    >
                        * = required
                    </Typography>
                </Grid>
            </Grid>
            {bigScreen && (
                <Box
                    sx={{
                        height: '100%',
                        justifyContent: 'center',
                        alignContent: 'center',
                    }}
                >
                    <UserAvatar {...avatarProps} />
                </Box>
            )}
        </Box>
    )
}
